import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FileAudio, Send, Mic, Filter, Download, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, FileText, BarChart3 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const PERIODI = [
  { value: 'settimana', label: 'Ultima settimana', days: 7 },
  { value: 'mese', label: 'Ultimo mese', days: 30 },
  { value: 'anno', label: 'Ultimo anno', days: 365 },
];

const periodStart = (periodo) => {
  const p = PERIODI.find(x => x.value === periodo) || PERIODI[1];
  const d = new Date();
  d.setDate(d.getDate() - p.days);
  return d;
};

export const Reports = () => {
  const { user, isAdmin } = useAuth();
  const [reports, setReports] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    attivita: '',
    clienti_gestiti: '',
    problemi: '',
    task_completati: ''
  });
  const [uploading, setUploading] = useState(false);
  const [filterDipendente, setFilterDipendente] = useState('');
  const [periodo, setPeriodo] = useState('mese');
  const [expanded, setExpanded] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      api.get('/tasks').then(r => setTasks(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const fetchReports = async () => {
    try {
      const response = await api.get('/reports');
      setReports(response.data);
    } catch (error) {
      toast.error('Errore caricamento report');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/reports', formData);
      toast.success('Report inviato con successo!');
      setFormData({
        attivita: '',
        clienti_gestiti: '',
        problemi: '',
        task_completati: ''
      });
      fetchReports();
    } catch (error) {
      toast.error('Errore invio report');
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataAudio = new FormData();
    formDataAudio.append('audio', file);

    try {
      await api.post('/reports/audio', formDataAudio, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Nota vocale inviata e trascritta!');
      fetchReports();
    } catch (error) {
      toast.error('Errore invio nota vocale');
    } finally {
      setUploading(false);
    }
  };

  /* ─── Periodo + filtri ─── */
  const start = periodStart(periodo);
  const inPeriod = (dateStr) => dateStr && new Date(dateStr) >= start;

  const filteredReports = reports
    .filter(r => inPeriod(r.data))
    .filter(r => !filterDipendente || r.dipendente_nome === filterDipendente);

  /* ─── Task completati per dipendente (nel periodo) ─── */
  const completedTasks = tasks.filter(t => t.stato === 'completato' && inPeriod(t.updated_at || t.created_at));
  const chartData = Object.entries(
    completedTasks.reduce((acc, t) => {
      const name = t.assignee_nome || 'Non assegnato';
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, count]) => ({ name: name.split(' ')[0], fullName: name, completati: count }))
   .sort((a, b) => b.completati - a.completati);

  /* ─── Metriche compatte per dipendente ─── */
  const dipendenti = [...new Set([
    ...reports.filter(r => inPeriod(r.data)).map(r => r.dipendente_nome).filter(Boolean),
    ...completedTasks.map(t => t.assignee_nome).filter(Boolean),
  ])].sort();

  const metriche = dipendenti.map(nome => {
    const repts = reports.filter(r => r.dipendente_nome === nome && inPeriod(r.data));
    return {
      nome,
      nReport: repts.length,
      nProblemi: repts.filter(r => (r.problemi || '').trim()).length,
      nTaskCompletati: completedTasks.filter(t => t.assignee_nome === nome).length,
      ultimoReport: repts.length ? new Date(Math.max(...repts.map(r => new Date(r.data)))) : null,
    };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 md:p-8 lg:p-10" data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-2">
          {isAdmin ? 'Report Dipendenti' : 'Attività Giornaliera'}
        </h1>
        <p className="text-base text-slate-600">
          {isAdmin ? 'Panoramica visiva del team' : 'Inserisci il tuo report giornaliero'}
        </p>
      </div>

      {!isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Report Testuale</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="attivita">Attività Svolte</Label>
                <Textarea
                  id="attivita"
                  value={formData.attivita}
                  onChange={(e) => setFormData({ ...formData, attivita: e.target.value })}
                  placeholder="Descrivi le attività svolte oggi..."
                  required
                  rows={4}
                  data-testid="report-activities-input"
                />
              </div>

              <div>
                <Label htmlFor="clienti">Clienti Gestiti</Label>
                <Textarea
                  id="clienti"
                  value={formData.clienti_gestiti}
                  onChange={(e) => setFormData({ ...formData, clienti_gestiti: e.target.value })}
                  placeholder="Clienti con cui hai lavorato..."
                  rows={2}
                  data-testid="report-clients-input"
                />
              </div>

              <div>
                <Label htmlFor="problemi">Problemi Riscontrati</Label>
                <Textarea
                  id="problemi"
                  value={formData.problemi}
                  onChange={(e) => setFormData({ ...formData, problemi: e.target.value })}
                  placeholder="Eventuali problemi..."
                  rows={2}
                  data-testid="report-problems-input"
                />
              </div>

              <div>
                <Label htmlFor="task">Task Completati</Label>
                <Textarea
                  id="task"
                  value={formData.task_completati}
                  onChange={(e) => setFormData({ ...formData, task_completati: e.target.value })}
                  placeholder="Task completati oggi..."
                  rows={2}
                  data-testid="report-tasks-input"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="report-submit-button">
                <Send size={20} className="mr-2" />
                Invia Report
              </Button>
            </form>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Nota Vocale</h2>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Carica una nota vocale che verrà automaticamente trascritta e salvata come report.
              </p>

              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*,.mp3,.wav,.m4a,.ogg"
                  onChange={handleAudioUpload}
                  className="hidden"
                  data-testid="audio-upload-input"
                />
                <FileAudio size={48} className="mx-auto mb-4 text-slate-400" />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-audio-button"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Trascrizione in corso...
                    </>
                  ) : (
                    <>
                      <Mic size={20} className="mr-2" />
                      Carica Nota Vocale
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 mt-4">
                  Formati supportati: MP3, WAV, M4A, OGG
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900 font-medium mb-1">
                  🎤 Trascrizione Automatica
                </p>
                <p className="text-sm text-blue-700">
                  La tua nota vocale verrà trascritta automaticamente utilizzando OpenAI Whisper.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Filtri (periodo + dipendente) ─── */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex bg-white border border-slate-200 rounded-lg p-0.5" data-testid="report-periodo-filter">
          {PERIODI.map(p => (
            <button key={p.value} onClick={() => setPeriodo(p.value)} data-testid={`periodo-${p.value}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${periodo === p.value ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {isAdmin && (
          <Select value={filterDipendente || '_all'} onValueChange={(v) => setFilterDipendente(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-[200px] h-9" data-testid="filter-dipendente-select">
              <Filter size={14} className="mr-2" /><SelectValue placeholder="Tutti i dipendenti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutti i dipendenti</SelectItem>
              {[...new Set(reports.map(r => r.dipendente_nome).filter(Boolean))].sort().map(n => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* ─── Grafico + Metriche (solo admin) ─── */}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="report-chart">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={16} className="text-blue-600" />
              <h2 className="text-sm font-semibold text-slate-800">Task completati per dipendente</h2>
              <span className="text-[11px] text-slate-400">({PERIODI.find(p => p.value === periodo)?.label.toLowerCase()})</span>
            </div>
            {chartData.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">
                Nessun task completato nel periodo selezionato
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v) => [v, 'Task completati']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="completati" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="report-metrics">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Metriche per persona</h2>
            {metriche.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">Nessuna attività nel periodo</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {metriche.map(m => (
                  <div key={m.nome} className="flex items-center gap-3 py-2.5" data-testid={`metric-row-${m.nome}`}>
                    <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-bold flex-shrink-0">
                      {m.nome.split(' ').map(w => w[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate">{m.nome}</p>
                      <p className="text-[10px] text-slate-400">
                        {m.ultimoReport ? `Ultimo report: ${m.ultimoReport.toLocaleDateString('it-IT')}` : 'Nessun report nel periodo'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="flex items-center gap-1 text-slate-500" title="Report inviati"><FileText size={12} /> {m.nReport}</span>
                      <span className="flex items-center gap-1 text-emerald-600 font-semibold" title="Task completati"><CheckCircle2 size={12} /> {m.nTaskCompletati}</span>
                      {m.nProblemi > 0 && <span className="flex items-center gap-1 text-amber-600" title="Problemi segnalati"><AlertTriangle size={12} /> {m.nProblemi}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Elenco report compatto (espandibile) ─── */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Report Recenti <span className="text-sm font-normal text-slate-400">({filteredReports.length})</span></h2>

        <div className="space-y-2">
          {filteredReports.map(report => {
            const isOpen = !!expanded[report.id];
            return (
              <div
                key={report.id}
                data-testid={`report-item-${report.id}`}
                className="border border-slate-200 rounded-lg hover:border-blue-300 transition-colors"
              >
                <button
                  onClick={() => setExpanded(s => ({ ...s, [report.id]: !s[report.id] }))}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left"
                  data-testid={`report-toggle-${report.id}`}
                >
                  {isOpen ? <ChevronDown size={14} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                  <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-[10px] font-bold flex-shrink-0">
                    {(report.dipendente_nome || '?').split(' ').map(w => w[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{report.dipendente_nome}</span>
                      <span className="text-[11px] text-slate-400">{new Date(report.data).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      {report.audio_url && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full text-[10px] font-medium">
                          <FileAudio size={10} /> vocale
                        </span>
                      )}
                      {(report.problemi || '').trim() && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full text-[10px] font-medium">
                          <AlertTriangle size={10} /> problema
                        </span>
                      )}
                    </div>
                    {!isOpen && <p className="text-xs text-slate-500 truncate mt-0.5">{report.attivita}</p>}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pl-[60px] space-y-2">
                    {report.audio_url && (
                      <div className="flex items-center gap-2">
                        <audio controls className="h-8 flex-1 max-w-md" preload="none">
                          <source src={`/api${report.audio_url}`} />
                        </audio>
                        <a href={`/api${report.audio_url}`} download className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 text-purple-600" title="Scarica audio" data-testid={`download-audio-${report.id}`}>
                          <Download size={14} />
                        </a>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-0.5">Attività:</p>
                      <p className="text-sm text-slate-700">{report.attivita}</p>
                    </div>
                    {report.clienti_gestiti && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 mb-0.5">Clienti:</p>
                        <p className="text-sm text-slate-700">{report.clienti_gestiti}</p>
                      </div>
                    )}
                    {report.problemi && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 mb-0.5">Problemi:</p>
                        <p className="text-sm text-slate-700">{report.problemi}</p>
                      </div>
                    )}
                    {report.task_completati && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 mb-0.5">Task Completati:</p>
                        <p className="text-sm text-slate-700">{report.task_completati}</p>
                      </div>
                    )}
                    {report.trascrizione && report.audio_url && (
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 mb-0.5">Trascrizione:</p>
                        <p className="text-sm text-slate-600 italic">{report.trascrizione}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filteredReports.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              Nessun report nel periodo selezionato
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
